import React, { useEffect, useState, useCallback, useRef } from "react";
import { httpService } from "../../services/httpService";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FileUpload from "../common/FileUpload";
import config from "../../config";

const S3_BASE_URL = (
  config.S3_BASE_URL ||
  process.env.REACT_APP_S3_BASE_URL ||
  "https://flps2025.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const resolveS3Url = (maybeRelativePath) => {
  if (!maybeRelativePath) return "";
  if (/^https?:\/\//i.test(maybeRelativePath)) return maybeRelativePath;
  const relative = String(maybeRelativePath).replace(/^\/+/, "");
  return `${S3_BASE_URL}/${relative}`;
};

const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// ─── Small reusable components ─────────────────────────────────────────────

const SectionCard = ({ title, children, className = "" }) => (
  <div className={`bg-slate-50 rounded-xl p-4 border border-slate-200 h-full ${className}`}>
    {title && (
      <h3 className="text-lg font-semibold mb-3 text-blue-700">{title}</h3>
    )}
    {children}
  </div>
);

const FieldRow = ({ label, viewValue, editNode, error, hint }) => (
  <div className="flex flex-col gap-0.5">
    <div className="flex items-start gap-2">
      <span className="text-xs text-gray-500 min-w-[100px] pt-1.5 shrink-0">{label}</span>
      <div className="flex-1">
        {editNode ?? (
          <span className="text-sm font-medium text-gray-800 block pt-1.5">
            {viewValue || "—"}
          </span>
        )}
        {error && <p className="text-red-500 text-xs mt-0.5">{error}</p>}
        {hint && !error && <p className="text-gray-400 text-xs mt-0.5">{hint}</p>}
      </div>
    </div>
  </div>
);

const inputCls = (hasError) =>
  `w-full border ${
    hasError ? "border-red-400 focus:ring-red-200" : "border-gray-300 focus:ring-blue-200 focus:border-blue-400"
  } rounded-lg px-2 py-1 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 transition`;

// ───────────────────────────────────────────────────────────────────────────

const SupplierOverview = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // ✅ QR code isolated from editValues — prevents FileUpload re-render loop
  const qrCodeUrlRef = useRef("");
  const qrInitialUrls = useRef([]);

  // ─── Fetch ──────────────────────────────────────────────────────────────

  const buildEditValues = (s) => {
    const upiInfo =
      s.upi_info && s.upi_info.length > 0
        ? s.upi_info[0]
        : { upi_id: "", qr_code: "" };
    const accountInfo =
      s.account_info && s.account_info.length > 0 ? s.account_info[0] : {};
    return {
      name: s.name || "",
      business_name: s.business_name || "", // Get business_name from API
      pan_number: s.pan_number || "",
      gst_number: s.gst_number || "",
      phone: s.contact_info?.phone || "",
      email: s.contact_info?.email || "",
      address: s.contact_info?.address || "",
      bank_name: accountInfo.bank_name || "",
      bank_account_number: accountInfo.bank_account_number || "",
      bank_ifsc_code: accountInfo.bank_ifsc_code || "",
      bank_branch: accountInfo.bank_branch || "",
      upi_id: upiInfo.upi_id || "",
    };
  };

  useEffect(() => {
    if (!supplierId) {
      setError("No supplierId provided.");
      setLoading(false);
      return;
    }
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await httpService.get(
          `/vendorsupplier/getVendorSupplier/${supplierId}`
        );
        if (response.status && response.code === 200) {
          const s = response.data;
          if (s) {
            setSupplier(s);
            setEditValues(buildEditValues(s));
            const upiInfo =
              s.upi_info && s.upi_info.length > 0 ? s.upi_info[0] : {};
            const existingQr = resolveS3Url(upiInfo.qr_code || "");
            qrCodeUrlRef.current = existingQr;
            qrInitialUrls.current = existingQr ? [existingQr] : [];
          } else {
            setError("Supplier not found");
          }
        } else {
          setError(response.message || "Failed to fetch supplier data");
        }
      } catch (err) {
        setError(err.message || "An error occurred while fetching supplier");
      } finally {
        setLoading(false);
      }
    };
    fetchSupplier();
  }, [supplierId]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleChange = (field, value) => {
    if (field === "pan_number") value = value.toUpperCase();
    if (field === "phone") value = value.replace(/\D/g, "").slice(0, 10);

    setEditValues((prev) => ({ ...prev, [field]: value }));

    setValidationErrors((prev) => {
      const next = { ...prev };
      if (field === "name") {
        if (!value?.trim() || value.length < 3)
          next.name = "Name must be at least 3 characters";
        else delete next.name;
      }
      if (field === "phone") {
        if (value && value.length !== 10)
          next.phone = "Phone must be exactly 10 digits";
        else delete next.phone;
      }
      if (field === "pan_number") {
        if (value && value.length > 0) {
          if (value.length !== 10)
            next.pan_number = "PAN must be exactly 10 characters";
          else if (!panRegex.test(value))
            next.pan_number = "Invalid PAN format (e.g., ABCDE1234F)";
          else delete next.pan_number;
        } else {
          delete next.pan_number;
        }
      }
      return next;
    });
  };

  // ✅ Writes only to ref — zero re-renders, FileUpload never re-uploads
  const handleUploadComplete = useCallback((urls) => {
    const firstUrl =
      Array.isArray(urls) && urls.length > 0 ? resolveS3Url(urls[0]) : "";
    qrCodeUrlRef.current = firstUrl;
  }, []);

  const handleEdit = () => {
    setEditMode(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    if (supplier) {
      setEditValues(buildEditValues(supplier));
      const upiInfo =
        supplier.upi_info && supplier.upi_info.length > 0
          ? supplier.upi_info[0]
          : {};
      const savedQr = resolveS3Url(upiInfo.qr_code || "");
      qrCodeUrlRef.current = savedQr;
      qrInitialUrls.current = savedQr ? [savedQr] : [];
    }
    setValidationErrors({});
    setEditMode(false);
  };

  const handleSave = async () => {
    const errors = {};
    if (!editValues.name?.trim() || editValues.name.length < 3)
      errors.name = "Name must be at least 3 characters";
    if (!editValues.phone?.trim())
      errors.phone = "Phone is required";
    else if (!/^\d{10}$/.test(editValues.phone))
      errors.phone = "Phone must be exactly 10 digits";
    if (editValues.pan_number?.trim()) {
      if (editValues.pan_number.length !== 10)
        errors.pan_number = "PAN must be exactly 10 characters";
      else if (!panRegex.test(editValues.pan_number))
        errors.pan_number = "Invalid PAN format (e.g., ABCDE1234F)";
    }

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the errors before saving");
      return;
    }

    const qrCode = qrCodeUrlRef.current;
    const payload = {
      name: editValues.name,
      business_name: editValues.business_name,
      pan_number: editValues.pan_number,
      gst_number: editValues.gst_number,
      contact_info: {
        phone: editValues.phone,
        email: editValues.email,
        address: editValues.address,
      },
      account_info: [
        {
          bank_name: editValues.bank_name || null,
          bank_account_number: editValues.bank_account_number || null,
          bank_ifsc_code: editValues.bank_ifsc_code || null,
          bank_branch: editValues.bank_branch || null,
        },
      ],
      upi_info:
        editValues.upi_id || qrCode
          ? [{ upi_id: editValues.upi_id || null, qr_code: qrCode || null }]
          : [],
    };

    try {
      setSaving(true);
      const response = await httpService.put(
        `/vendorsupplier/updateVendorSupplier/${supplierId}`,
        payload
      );
      if (response.status && response.code === 200) {
        setSupplier((prev) => ({
          ...prev,
          ...payload,
          contact_info: payload.contact_info,
        }));
        qrInitialUrls.current = qrCode ? [qrCode] : [];
        setEditMode(false);
        toast.success("Supplier updated successfully");
      } else {
        toast.error(response.message || "Failed to update supplier");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An error occurred while updating supplier"
      );
    } finally {
      setSaving(false);
    }
  };

  // ─── Early returns ───────────────────────────────────────────────────────

  if (loading)
    return (
      <div className="p-8 text-gray-500 bg-white rounded-xl shadow text-center">
        Loading supplier details...
      </div>
    );
  if (error)
    return (
      <div className="p-8 text-red-500 bg-white rounded-xl shadow">{error}</div>
    );
  if (!supplier)
    return (
      <div className="p-8 text-gray-400 italic bg-white rounded-xl shadow">
        No supplier found.
      </div>
    );

  const upiInfo =
    supplier.upi_info && supplier.upi_info.length > 0
      ? supplier.upi_info[0]
      : {};
  const accountInfo =
    supplier.account_info && supplier.account_info.length > 0
      ? supplier.account_info[0]
      : {};

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 h-[calc(100vh-80px)] overflow-y-auto">

      {/* Page Header with Back Button */}
      <div className="flex items-center justify-between mb-5 relative">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h2 className="text-xl font-bold text-blue-800 tracking-tight">
              Supplier Profile
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!editMode ? (
            <button
              onClick={handleEdit}
              className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg shadow transition"
            >
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-1.5 border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 rounded-lg text-sm font-semibold transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold shadow transition disabled:opacity-50 flex items-center gap-2"
              >
                {saving && (
                  <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Two Column Grid Layout (Box 1-4) with inner 2-column grids ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Box 1: Basic Information */}
        <SectionCard title="Basic Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldRow
              label="Supplier Name *"
              viewValue={supplier.name}
              error={validationErrors.name}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(validationErrors.name)}
                    value={editValues.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Enter supplier name"
                  />
                ) : null
              }
            />
            <FieldRow
              label="Phone *"
              viewValue={supplier.contact_info?.phone}
              error={validationErrors.phone}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(validationErrors.phone)}
                    value={editValues.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="10-digit phone number"
                    inputMode="numeric"
                    maxLength={10}
                  />
                ) : null
              }
            />
            <FieldRow
              label="Email"
              viewValue={supplier.contact_info?.email}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="Enter email"
                    type="email"
                  />
                ) : null
              }
            />
            <FieldRow
              label="Address"
              viewValue={supplier.contact_info?.address}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    placeholder="Enter address"
                  />
                ) : null
              }
            />
          </div>
        </SectionCard>

        {/* Box 2: Business Information */}
        <SectionCard title="Business Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldRow
              label="Business Name"
              viewValue={supplier.business_name || "-"}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.business_name}
                    onChange={(e) => handleChange("business_name", e.target.value)}
                    placeholder="Enter business/company name"
                  />
                ) : null
              }
            />
            <FieldRow
              label="PAN"
              viewValue={supplier.pan_number}
              error={validationErrors.pan_number}
              hint={editMode ? "Format: ABCDE1234F" : null}
              editNode={
                editMode ? (
                  <input
                    className={`${inputCls(validationErrors.pan_number)} uppercase`}
                    value={editValues.pan_number}
                    onChange={(e) => handleChange("pan_number", e.target.value)}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                ) : null
              }
            />
            <FieldRow
              label="GST Number"
              viewValue={supplier.gst_number}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.gst_number}
                    onChange={(e) => handleChange("gst_number", e.target.value)}
                    placeholder="Enter GST number"
                  />
                ) : null
              }
            />
          </div>
        </SectionCard>

        {/* Box 3: Payment Information */}
        <SectionCard title="Payment Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldRow
              label="UPI ID"
              viewValue={upiInfo.upi_id}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.upi_id}
                    onChange={(e) => handleChange("upi_id", e.target.value)}
                    placeholder="e.g. name@bank"
                  />
                ) : null
              }
            />
            <div>
              <span className="text-xs text-gray-500 min-w-[100px] block mb-1">QR Code</span>
              {editMode ? (
                <FileUpload
                  uploadEndpoint="/upload/fileUpload"
                  multiple={false}
                  maxFiles={1}
                  moduleName="vendors"
                  docType="suppliers/payment-qr"
                  existingUrls={qrInitialUrls.current}
                  onUploadComplete={handleUploadComplete}
                />
              ) : qrCodeUrlRef.current ? (
                <div className="flex flex-col items-start gap-1">
                  <img
                    src={qrCodeUrlRef.current}
                    alt="UPI QR Code"
                    className="w-20 h-20 object-contain border border-gray-200 rounded-lg bg-white p-1 shadow-sm"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <span className="text-xs text-gray-400">
                    Click Edit to replace
                  </span>
                </div>
              ) : (
                <span className="text-sm text-gray-400 italic pt-1 block">
                  No QR code uploaded
                </span>
              )}
            </div>
          </div>
        </SectionCard>

        {/* Box 4: Bank Account Information */}
        <SectionCard title="Bank Account Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldRow
              label="Bank Name"
              viewValue={accountInfo.bank_name}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.bank_name}
                    onChange={(e) => handleChange("bank_name", e.target.value)}
                    placeholder="Enter bank name"
                  />
                ) : null
              }
            />
            <FieldRow
              label="Account Number"
              viewValue={accountInfo.bank_account_number}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.bank_account_number}
                    onChange={(e) =>
                      handleChange("bank_account_number", e.target.value)
                    }
                    placeholder="Enter account number"
                    inputMode="numeric"
                  />
                ) : null
              }
            />
            <FieldRow
              label="IFSC Code"
              viewValue={accountInfo.bank_ifsc_code}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.bank_ifsc_code}
                    onChange={(e) =>
                      handleChange("bank_ifsc_code", e.target.value)
                    }
                    placeholder="Enter IFSC code"
                  />
                ) : null
              }
            />
            <FieldRow
              label="Branch"
              viewValue={accountInfo.bank_branch}
              editNode={
                editMode ? (
                  <input
                    className={inputCls(false)}
                    value={editValues.bank_branch}
                    onChange={(e) => handleChange("bank_branch", e.target.value)}
                    placeholder="Enter branch name"
                  />
                ) : null
              }
            />
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default SupplierOverview;